/**
 * Live run subscription (prompt.md §48, §49, §76, §77).
 *
 * WHY this lives in the shared core rather than in the conversation screen: the
 * web SPA and the Tauri shell must reconnect identically, and Android mirrors
 * the same rules. The hook owns three things a screen must not re-implement —
 * the resume cursor, the reducer, and the reconciliation between the streamed
 * buffer and the durable transcript.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isTerminal, type ConversationId, type RunId } from "@dayvise/domain";
import { createPollingTransport, createRunStream, createWebSocketTransport, type RealtimeState, type RunStream } from "@dayvise/client-sdk";

import { EMPTY_LIVE_RUN, applyRunEvent, beginRun, markSnapshotRequired, reconcileWithTranscript, mergeRecordedToolCalls, mergeRecordedClarifications, type LiveRunState } from "./runStream.ts";
import { queryKeys } from "./queryKeys.ts";
import { useActiveWorkspaceId, useDayviseClient } from "./providers.tsx";

/**
 * How many failed socket sessions to tolerate before polling instead.
 *
 * Three rather than one: a single failed upgrade is usually a transient blip, and polling is
 * the heavier transport per client. Three consecutive failures is a network that means it.
 */
const SOCKET_ATTEMPTS_BEFORE_POLLING = 3;

export interface UseLiveRunInput {
  readonly runId: RunId | null;
  readonly conversationId?: ConversationId;
  /** Sequence to resume from. `-1` asks for the whole buffer. */
  readonly afterSequence?: number;
  /** Transcript message ids, used to drop a streamed buffer once it is durable. */
  readonly transcriptMessageIds?: readonly string[];
  /** Where the realtime endpoint lives; omit to use polling only. */
  readonly websocketUrl?: (runId: RunId, afterSequence: number) => string;
  readonly enabled?: boolean;
}

export interface UseLiveRunResult {
  readonly live: LiveRunState;
  readonly status: RealtimeState;
  readonly snapshotRequired: boolean;
  readonly error: Error | null;
  /** Force a transcript refetch; used when the buffer no longer covers the cursor. */
  readonly resync: () => void;
}

/**
 * Subscribe to a run.
 *
 * A WebSocket transport is preferred when the deployment provides one; the
 * polling transport is the fallback that keeps the same reducer and cursor
 * semantics, which is what lets a client on a restricted network behave like one
 * that is not.
 */
export function useLiveRun(input: UseLiveRunInput): UseLiveRunResult {
  const client = useDayviseClient();
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId();
  const [live, setLive] = useState<LiveRunState>(EMPTY_LIVE_RUN);
  const liveRef = useRef(live);
  liveRef.current = live;
  const [status, setStatus] = useState<RealtimeState>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [resyncCounter, setResyncCounter] = useState(0);
  // Set once the socket has failed often enough to stop being worth retrying.
  //
  // Android already degrades this way (`openSocketWithRetries` then `pollWindow`). The web
  // built one transport or the other and never moved between them, so a network that allows
  // HTTPS but blocks the upgrade left the transcript at "Reconnecting..." indefinitely while
  // a working alternative sat unused.
  const [fellBackToPolling, setFellBackToPolling] = useState(false);
  const socketFailures = useRef(0);
  const streamRef = useRef<RunStream | null>(null);
  const enabled = input.enabled ?? true;
  // The latest URL builder, kept out of the memo deps so transport identity is
  // stable across renders (see the transport memo below).
  const websocketUrlRef = useRef(input.websocketUrl);
  websocketUrlRef.current = input.websocketUrl;
  const hasWebsocketUrl = input.websocketUrl !== undefined
    && !(live.runId === input.runId && live.state && isTerminal(live.state));

  const transport = useMemo(() => {
    if (hasWebsocketUrl && !fellBackToPolling) {
      // Read the URL builder through a ref, not through the dependency array: a
      // caller that passes a fresh closure each render (e.g. one closing over a
      // refreshed ticket) must not rebuild the transport, because the connect
      // effect keys on `transport` and every rebuild tears the socket down and
      // reconnects. The builder is still consulted per connection, so a refreshed
      // ticket is used on the next connect.
      return createWebSocketTransport({
        url: (runId, afterSequence) => websocketUrlRef.current?.(runId as RunId, afterSequence) ?? "",
      });
    }
    return createPollingTransport({
      readEvents: (runId, afterSequence) => client.runs.events(runId, afterSequence),
      // The polling transport wants the run itself, not the fuller detail view.
      readRun: async (runId) => (await client.runs.get(runId)).run,
    });
  }, [client, hasWebsocketUrl, fellBackToPolling]);

  const refreshDurable = useCallback(async () => {
    if (!input.runId) return;
    const scope = { workspaceId };
    await Promise.all([
      input.conversationId ? queryClient.invalidateQueries({ queryKey: queryKeys.conversation(scope, input.conversationId) }, { throwOnError: true }) : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: queryKeys.runApprovals(scope, input.runId) }, { throwOnError: true }),
    ]);
  }, [queryClient, workspaceId, input.conversationId, input.runId]);

  useEffect(() => {
    if (!enabled || input.runId === null) {
      setLive(EMPTY_LIVE_RUN);
      setStatus("idle");
      return;
    }

    let disposed = false;
    let recovery: { touchedCalls: Set<string> } | null = null;
    setLive((previous) => beginRun(input.runId as RunId, previous));
    const stream = createRunStream({ transport });
    streamRef.current = stream;

    const offEvents = stream.onEvent((event) => {
      if (recovery) {
        const payload = event.payload as Record<string, unknown>;
        for (const id of [payload.toolCallId, payload.clarificationId]) {
          if (typeof id === "string") recovery.touchedCalls.add(id);
        }
      }
      setLive((previous) => applyRunEvent(beginRun(input.runId as RunId, previous), event));
      if (["assistant.message.completed", "run.completed", "run.waiting_approval", "approval.received", "clarification.received"].includes(event.type)) {
        void refreshDurable().catch((caught: unknown) => { if (!disposed) setError(caught instanceof Error ? caught : new Error(String(caught))); });
      }
    });
    const offState = stream.onStateChange((change) => {
      setStatus(change.state);
      setError(change.error === null ? null : new Error(change.error));
      // A reconnect means a session failed. Count them, and after a few stop asking for an
      // upgrade this network evidently will not allow: polling reaches the same server over
      // the HTTPS it is already permitting, and both transports share the reducer and the
      // resume cursor, so the transcript continues rather than restarting.
      if (change.state === "reconnecting" && hasWebsocketUrl && !fellBackToPolling) {
        socketFailures.current += 1;
        if (socketFailures.current >= SOCKET_ATTEMPTS_BEFORE_POLLING) setFellBackToPolling(true);
      }
    });
    const offSnapshot = stream.onSnapshotRequired((latestSequence) => {
      const request = { touchedCalls: new Set<string>() };
      recovery = request;
      setLive((previous) => markSnapshotRequired(beginRun(input.runId as RunId, previous), latestSequence));
      void Promise.all([refreshDurable(), client.runs.get(input.runId as RunId)]).then(([, detail]) => {
        if (disposed || recovery !== request) return;
        recovery = null;
        // Traffic can continue while the snapshot loads. Preserve calls changed
        // by those events, but still recover unrelated missing prompts/tools.
        const recorded = detail.toolCalls.filter((call) => !request.touchedCalls.has(call.toolCallId ?? ""));
        setLive((previous) => {
          if (previous.runId !== input.runId) return previous;
          const merged = mergeRecordedClarifications(mergeRecordedToolCalls(previous, recorded), recorded);
          return { ...merged,
            state: previous.cursor > latestSequence ? previous.state : detail.run.state,
            pauseReason: previous.cursor > latestSequence ? previous.pauseReason : detail.run.pauseReason,
            snapshotRequired: false,
          };
        });
        if (isTerminal(detail.run.state) && liveRef.current.cursor <= latestSequence) stream.close();
      }).catch((caught: unknown) => { if (!disposed) setError(caught instanceof Error ? caught : new Error(String(caught))); });
    });

    void stream.connect({ runId: input.runId, afterSequence: input.afterSequence ?? (liveRef.current.runId === input.runId ? liveRef.current.cursor : -1) }).catch((caught: unknown) => {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    });

    return () => {
      disposed = true;
      offEvents();
      offState();
      offSnapshot();
      stream.close();
      streamRef.current = null;
    };
  }, [enabled, input.runId, input.afterSequence, transport, resyncCounter, refreshDurable, client]);

  // A committed message supersedes whatever the stream had buffered for it.
  useEffect(() => {
    if (input.transcriptMessageIds === undefined) return;
    setLive((previous) => reconcileWithTranscript(previous, input.transcriptMessageIds as readonly string[]));
  }, [input.transcriptMessageIds]);

  const resync = useCallback(() => {
    void refreshDurable().then(() => setResyncCounter((value) => value + 1)).catch((caught: unknown) => setError(caught instanceof Error ? caught : new Error(String(caught))));
  }, [refreshDurable]);

  const current = live.runId === input.runId ? live : EMPTY_LIVE_RUN;
  const pendingText = current.pendingMessages.filter((message) => !input.transcriptMessageIds?.includes(message.messageId)).map((message) => message.text);
  const visible = { ...current, streamingText: [...pendingText, current.streamingText].filter(Boolean).join("\n\n") };
  return { live: visible, status, snapshotRequired: current.snapshotRequired, error, resync };
}

/** The highest sequence a client has observed, for a later reconnect. */
export function resumeCursor(live: LiveRunState): number {
  return live.cursor;
}
